import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import CalendarPicker from '../util/CalendarPicker';

const MODAL_SX = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px',
  boxShadow: 24, p: 3,
};

const TASK_STATUS = ['todo', 'in_progress', 'done'];
const TASK_STATUS_LABELS: Record<string, string> = { todo: 'To Do', in_progress: 'In Progress', done: 'Done' };
const TASK_STATUS_COLORS: Record<string, string> = {
  todo: 'var(--text-color-calendar-week)',
  in_progress: 'var(--color-warning)',
  done: 'var(--color-success)',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', on_hold: 'var(--color-warning)',
  completed: 'var(--color-info)', archived: 'var(--text-color-calendar-week)',
};

const STATUS_BG_COLORS: Record<string, string> = {
  active: 'var(--color-success-bg)', on_hold: 'var(--color-warning-bg)',
  completed: 'var(--color-info-bg)', archived: 'var(--btn-user-in)',
};
const STATUS_LABELS: Record<string, string> = {
  active: 'Active', on_hold: 'On Hold', completed: 'Completed', archived: 'Archived',
};

// ── Inline form helpers ──

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col mb-[10px]">
      <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input';

// ── Task Modal ──

function TaskModal({ open, onClose, onSaved, projectId, editTask, users }: {
  open: boolean; onClose: () => void; onSaved: () => void;
  projectId: number; editTask?: any; users: any[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('todo');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  useEffect(() => {
    if (editTask) {
      setTitle(editTask.title ?? '');
      setDescription(editTask.description ?? '');
      setStatus(editTask.status ?? 'todo');
      setDueDate(editTask.dueDate ? String(editTask.dueDate).slice(0, 10) : '');
      setAssigneeId(editTask.assigneeId ? String(editTask.assigneeId) : '');
    } else {
      setTitle(''); setDescription(''); setStatus('todo'); setDueDate(''); setAssigneeId('');
    }
  }, [editTask, open]);

  const handleSave = async () => {
    if (!title.trim()) { enqueueSnackbar(t('Required'), { variant: 'error' }); return; }
    const payload = {
      title: title.trim(), description: description.trim() || null,
      status, dueDate: dueDate || null,
      assigneeId: assigneeId ? Number(assigneeId) : null,
    };
    try {
      if (editTask?.id) {
        await axios.put(`crm/projects/${projectId}/tasks/${editTask.id}`, payload);
      } else {
        await axios.post(`crm/projects/${projectId}/tasks`, payload);
      }
      onSaved(); onClose();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={MODAL_SX}>
          <div className="text-[15px] font-semibold mb-[14px] text-[var(--text-color)]">
            {editTask ? 'Edit Task' : 'Add Task'}
          </div>
          <Field label="Title">
            <input title="Title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-x-[10px]">
            <Field label="Status">
              <select title="Status" className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
                value={status} onChange={(e) => setStatus(e.target.value)}>
                {TASK_STATUS.map((s) => <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>)}
              </select>
            </Field>
            <Field label="Due date">
              <CalendarPicker mode="single" variant="field" output="string" value={dueDate} onChange={(v) => setDueDate(v)} />
            </Field>
          </div>
          <Field label="Assignee">
            <select title="Assignee" className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
              value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">— Not assigned —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea title="Description" className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="flex gap-[8px] mt-[4px]">
            <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }} onClick={handleSave}>
              {editTask ? t('Change') : t('Add')}
            </Button>
            <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }} onClick={onClose}>{t('Cancel')}</Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

// ── Event Modal ──

function EventModal({ open, onClose, onSaved, projectId, editEvent }: {
  open: boolean; onClose: () => void; onSaved: () => void;
  projectId: number; editEvent?: any;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');

  useEffect(() => {
    if (editEvent) {
      setTitle(editEvent.title ?? '');
      setDescription(editEvent.description ?? '');
      setDate(editEvent.date ? String(editEvent.date).slice(0, 16) : '');
      setLocation(editEvent.location ?? '');
    } else {
      setTitle(''); setDescription(''); setDate(''); setLocation('');
    }
  }, [editEvent, open]);

  const handleSave = async () => {
    if (!title.trim()) { enqueueSnackbar(t('Required'), { variant: 'error' }); return; }
    const payload = {
      title: title.trim(), description: description.trim() || null,
      date: date || null, location: location.trim() || null,
    };
    try {
      if (editEvent?.id) {
        await axios.put(`crm/projects/${projectId}/events/${editEvent.id}`, payload);
      } else {
        await axios.post(`crm/projects/${projectId}/events`, payload);
      }
      onSaved(); onClose();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={MODAL_SX}>
          <div className="text-[15px] font-semibold mb-[14px] text-[var(--text-color)]">
            {editEvent ? 'Edit Event' : 'Add Event'}
          </div>
          <Field label="Title">
            <input title="Title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-x-[10px]">
            <Field label="Date & time">
              <input title="Date" type="datetime-local" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Location">
              <input title="Location" className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} />
            </Field>
          </div>
          <Field label="Description">
            <textarea title="Description" className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="flex gap-[8px] mt-[4px]">
            <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }} onClick={handleSave}>
              {editEvent ? t('Change') : t('Add')}
            </Button>
            <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }} onClick={onClose}>{t('Cancel')}</Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

// ── Main detail ──

interface Props {
  project: any;
  onBack: () => void;
  onEdit: () => void;
}

type TabKey = 'tasks' | 'events';

export default function CrmProjectDetail({ project, onBack, onEdit }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<TabKey>('tasks');
  const [tasks, setTasks] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [taskModal, setTaskModal] = useState(false);
  const [editTask, setEditTask] = useState<any>(null);
  const [eventModal, setEventModal] = useState(false);
  const [editEvent, setEditEvent] = useState<any>(null);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`crm/projects/${project.id}/tasks`);
      setTasks(Array.isArray(data) ? data : []);
    } catch {}
  }, [project.id]);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`crm/projects/${project.id}/events`);
      setEvents(Array.isArray(data) ? data : []);
    } catch {}
  }, [project.id]);

  useEffect(() => {
    axios.get('users').then(({ data }) => setUsers(Array.isArray(data) ? data : data.data ?? [])).catch(() => {});
    loadTasks();
    loadEvents();
  }, [loadTasks, loadEvents]);

  const deleteTask = async (id: number) => {
    try {
      await axios.delete(`crm/projects/${project.id}/tasks/${id}`);
      loadTasks();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const deleteEvent = async (id: number) => {
    try {
      await axios.delete(`crm/projects/${project.id}/events/${id}`);
      loadEvents();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const cycleTaskStatus = async (task: any) => {
    const order = ['todo', 'in_progress', 'done'];
    const next = order[(order.indexOf(task.status) + 1) % order.length];
    try {
      await axios.put(`crm/projects/${project.id}/tasks/${task.id}`, { status: next });
      loadTasks();
    } catch {}
  };

  const color = project.color ?? 'var(--color-info)';
  const manager = project.manager
    ? `${project.manager.firstName ?? ''} ${project.manager.lastName ?? ''}`.trim()
    : null;

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'tasks', label: `${t('Tasks') || 'Tasks'} (${tasks.length})` },
    { key: 'events', label: `${t('Events') || 'Events'} (${events.length})` },
  ];

  const taskIcon = (status: string) => {
    if (status === 'done') return <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'var(--color-success)' }} />;
    if (status === 'in_progress') return <PendingOutlinedIcon sx={{ fontSize: 18, color: 'var(--color-warning)' }} />;
    return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'var(--text-color-calendar-week)' }} />;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-[10px] mb-[14px]">
        <IconButton size="small" onClick={onBack} sx={{ color: 'var(--text-color)' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <span className="w-[12px] h-[12px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-[16px] font-semibold text-[var(--text-color)] flex-1">{project.name}</span>
        <span className="text-[11px] font-medium px-[8px] py-[2px] rounded-full"
          style={{ backgroundColor: STATUS_BG_COLORS[project.status] ?? 'var(--btn-user-in)', color: STATUS_COLORS[project.status] ?? 'var(--text-color-calendar-week)' }}>
          {STATUS_LABELS[project.status] ?? project.status}
        </span>
        <IconButton size="small" onClick={onEdit} sx={{ color: 'var(--text-color)', opacity: 0.6 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Info row */}
      <div className="flex flex-wrap gap-[16px] mb-[16px] text-[12px] text-[var(--text-color-calendar-week)]">
        {manager && <span>👤 <span className="text-[var(--text-color)]">{manager}</span></span>}
        {project.organisation && <span>🏢 <span className="text-[var(--text-color)]">{project.organisation.name}</span></span>}
        {project.type && <span>🏷 <span className="text-[var(--text-color)]">{project.type}</span></span>}
        {project.startDate && <span>📅 {String(project.startDate).slice(0, 10)}{project.endDate ? ` – ${String(project.endDate).slice(0, 10)}` : ''}</span>}
        {project.contractValue && <span>💰 £{Number(project.contractValue).toLocaleString()}</span>}
      </div>

      {project.description && (
        <p className="text-[13px] text-[var(--text-color-calendar-week)] mb-[16px] leading-relaxed">{project.description}</p>
      )}

      {/* Tabs */}
      <div className="flex gap-[6px] mb-[14px]" style={{ borderBottom: '1px solid var(--border-input)', paddingBottom: 8 }}>
        {tabs.map(({ key, label }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`text-[13px] px-[14px] py-[5px] rounded-full border transition-colors ${
              tab === key
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-transparent text-[var(--text-color)] border-[var(--border-input)]'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Tasks tab */}
      {tab === 'tasks' && (
        <div>
          <div className="flex justify-end mb-[10px]">
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              sx={{ fontSize: 12 }} onClick={() => { setEditTask(null); setTaskModal(true); }}>
              Add Task
            </Button>
          </div>

          {tasks.length === 0 ? (
            <div className="text-center text-[13px] text-[var(--text-color-calendar-week)] py-[30px]">No tasks yet</div>
          ) : (
            <div className="flex flex-col gap-[6px]">
              {tasks.map((task) => (
                <div key={task.id}
                  className="flex items-center gap-[10px] p-[10px] rounded-[5px] border"
                  style={{ borderColor: 'var(--border-input)', backgroundColor: 'var(--bg-form)' }}>
                  <button type="button" onClick={() => cycleTaskStatus(task)} className="flex-shrink-0">
                    {taskIcon(task.status)}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[13px] font-medium ${task.status === 'done' ? 'line-through opacity-50' : 'text-[var(--text-color)]'}`}>
                      {task.title}
                    </div>
                    <div className="flex gap-[10px] mt-[2px] text-[11px] text-[var(--text-color-calendar-week)]">
                      <span style={{ color: TASK_STATUS_COLORS[task.status] }}>{TASK_STATUS_LABELS[task.status]}</span>
                      {task.dueDate && <span>Due {String(task.dueDate).slice(0, 10)}</span>}
                      {task.assignee && <span>👤 {task.assignee.firstName} {task.assignee.lastName}</span>}
                    </div>
                  </div>
                  <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }}
                    onClick={() => { setEditTask(task); setTaskModal(true); }}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }}
                    onClick={() => deleteTask(task.id)}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Events tab */}
      {tab === 'events' && (
        <div>
          <div className="flex justify-end mb-[10px]">
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              sx={{ fontSize: 12 }} onClick={() => { setEditEvent(null); setEventModal(true); }}>
              Add Event
            </Button>
          </div>

          {events.length === 0 ? (
            <div className="text-center text-[13px] text-[var(--text-color-calendar-week)] py-[30px]">No events yet</div>
          ) : (
            <div className="flex flex-col gap-[6px]">
              {events.map((ev) => (
                <div key={ev.id}
                  className="flex items-start gap-[12px] p-[12px] rounded-[5px] border"
                  style={{ borderColor: 'var(--border-input)', backgroundColor: 'var(--bg-form)' }}>
                  <div className="flex-shrink-0 w-[40px] text-center">
                    {ev.date ? (
                      <>
                        <div className="text-[10px] text-[var(--text-color-calendar-week)] uppercase">
                          {new Date(ev.date).toLocaleString('en', { month: 'short' })}
                        </div>
                        <div className="text-[18px] font-bold text-[var(--text-color)] leading-none">
                          {new Date(ev.date).getDate()}
                        </div>
                      </>
                    ) : (
                      <div className="text-[20px]">📌</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[var(--text-color)]">{ev.title}</div>
                    <div className="flex gap-[10px] mt-[2px] text-[11px] text-[var(--text-color-calendar-week)]">
                      {ev.date && (
                        <span>{new Date(ev.date).toLocaleString('en', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                      {ev.location && <span>📍 {ev.location}</span>}
                    </div>
                    {ev.description && (
                      <p className="text-[12px] text-[var(--text-color-calendar-week)] mt-[4px]">{ev.description}</p>
                    )}
                  </div>
                  <div className="flex gap-[2px] flex-shrink-0">
                    <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }}
                      onClick={() => { setEditEvent(ev); setEventModal(true); }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: 'var(--text-color)', opacity: 0.5 }}
                      onClick={() => deleteEvent(ev.id)}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <TaskModal
        open={taskModal}
        onClose={() => setTaskModal(false)}
        onSaved={loadTasks}
        projectId={project.id}
        editTask={editTask}
        users={users}
      />
      <EventModal
        open={eventModal}
        onClose={() => setEventModal(false)}
        onSaved={loadEvents}
        projectId={project.id}
        editEvent={editEvent}
      />
    </div>
  );
}
